import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import api from '@/services/api'

// API Base Path for Admin endpoints
const API_BASE = '/v1/admin'

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN_EXPIRES_AT: 'token_expires_at',
  USER: 'user',
  REQUIRES_SETUP_2FA: 'requires_setup_2fa'
}

/**
 * Safely parse JSON from localStorage
 */
function safeParseJSON(value, fallback = null) {
  if (!value || value === 'undefined' || value === 'null') {
    return fallback
  }
  try {
    return JSON.parse(value)
  } catch (e) {
    console.warn('Failed to parse stored JSON:', e)
    return fallback
  }
}

/**
 * Safely get item from localStorage
 */
function getStorageItem(key, fallback = null) {
  try {
    const value = localStorage.getItem(key)
    return value !== null && value !== 'undefined' && value !== 'null' ? value : fallback
  } catch (e) {
    console.warn(`Failed to get ${key} from localStorage:`, e)
    return fallback
  }
}

/**
 * Safely set item in localStorage
 */
function setStorageItem(key, value) {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
    }
  } catch (e) {
    console.warn(`Failed to set ${key} in localStorage:`, e)
  }
}

/**
 * Safely remove item from localStorage
 */
function removeStorageItem(key) {
  try {
    localStorage.removeItem(key)
  } catch (e) {
    console.warn(`Failed to remove ${key} from localStorage:`, e)
  }
}

/**
 * Clear all auth-related storage
 */
function clearAuthStorage() {
  Object.values(STORAGE_KEYS).forEach(key => removeStorageItem(key))
}

export const useAuthStore = defineStore('auth', () => {
  // ============================================
  // STATE - Initialize from localStorage
  // ============================================
  const user = ref(null)
  const accessToken = ref(null)
  const refreshToken = ref(null)
  const tokenExpiresAt = ref(null)
  const pending2FA = ref(false)
  const requiresSetup2FA = ref(false)
  const tempAdminId = ref(null)
  const setup2FAData = ref(null)
  const isInitialized = ref(false)
  const isInitializing = ref(false)

  // ============================================
  // COMPUTED PROPERTIES
  // ============================================
  const isAuthenticated = computed(() => {
    return !!(accessToken.value && user.value && user.value.id)
  })
  
  const token = computed(() => accessToken.value)
  const userRole = computed(() => user.value?.role || null)
  const isSuperAdmin = computed(() => userRole.value === 'super_admin')
  const isAdmin = computed(() => ['super_admin', 'admin'].includes(userRole.value))
  const isContentManager = computed(() => userRole.value === 'content_manager')
  const isReviewer = computed(() => userRole.value === 'reviewer')

  // ============================================
  // WATCHERS - Persist state changes to localStorage
  // ============================================
  watch(user, (newUser) => {
    if (newUser && typeof newUser === 'object' && newUser.id) {
      setStorageItem(STORAGE_KEYS.USER, newUser)
    } else if (newUser === null) {
      removeStorageItem(STORAGE_KEYS.USER)
    }
  }, { deep: true })

  watch(accessToken, (newToken) => {
    if (newToken) {
      setStorageItem(STORAGE_KEYS.ACCESS_TOKEN, newToken)
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
    } else {
      removeStorageItem(STORAGE_KEYS.ACCESS_TOKEN)
      delete api.defaults.headers.common['Authorization']
    }
  })

  watch(refreshToken, (newToken) => {
    if (newToken) {
      setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, newToken)
    } else {
      removeStorageItem(STORAGE_KEYS.REFRESH_TOKEN)
    }
  })

  watch(tokenExpiresAt, (newExpiry) => {
    if (newExpiry) {
      setStorageItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, newExpiry)
    } else {
      removeStorageItem(STORAGE_KEYS.TOKEN_EXPIRES_AT)
    }
  })

  watch(requiresSetup2FA, (newVal) => {
    if (newVal) {
      setStorageItem(STORAGE_KEYS.REQUIRES_SETUP_2FA, 'true')
    } else {
      removeStorageItem(STORAGE_KEYS.REQUIRES_SETUP_2FA)
    }
  })

  // ============================================
  // INITIALIZATION
  // ============================================
  
  /**
   * Initialize auth state from localStorage
   * This should be called once when the app starts
   */
  async function initialize() {
    if (isInitialized.value || isInitializing.value) {
      return isAuthenticated.value
    }

    isInitializing.value = true

    try {
      // Restore state from localStorage
      const storedToken = getStorageItem(STORAGE_KEYS.ACCESS_TOKEN)
      const storedRefreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN)
      const storedExpiry = getStorageItem(STORAGE_KEYS.TOKEN_EXPIRES_AT)
      const storedUser = safeParseJSON(getStorageItem(STORAGE_KEYS.USER))

      // Validate stored data
      if (!storedToken || !storedRefreshToken) {
        clearAuth()
        return false
      }

      // Check if token is expired
      const expiryTime = storedExpiry ? parseInt(storedExpiry, 10) : 0
      const isExpired = expiryTime > 0 && Date.now() >= expiryTime

      if (isExpired) {
        // Try to refresh the token
        refreshToken.value = storedRefreshToken
        try {
          await refreshAccessToken()
          // After refresh, try to fetch user if we don't have one
          if (!storedUser || !storedUser.id) {
            await fetchUser()
          } else {
            user.value = storedUser
          }
          return true
        } catch (error) {
          console.warn('Token refresh failed during initialization:', error)
          clearAuth()
          return false
        }
      }

      // Token is still valid, restore state
      accessToken.value = storedToken
      refreshToken.value = storedRefreshToken
      tokenExpiresAt.value = expiryTime || null

      // Set auth header
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`

      // Restore or fetch user
      if (storedUser && storedUser.id) {
        user.value = storedUser
        // Optionally refresh user data in background
        fetchUser().catch(err => console.warn('Background user fetch failed:', err))
      } else {
        // No stored user, try to fetch
        const success = await fetchUser()
        if (!success) {
          clearAuth()
          return false
        }
      }

      return true
    } catch (error) {
      console.error('Auth initialization error:', error)
      clearAuth()
      return false
    } finally {
      isInitialized.value = true
      isInitializing.value = false
    }
  }

  /**
   * Hydrate state from localStorage without async operations
   * Used for immediate state restoration
   */
  function hydrate() {
    const storedToken = getStorageItem(STORAGE_KEYS.ACCESS_TOKEN)
    const storedRefreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN)
    const storedExpiry = getStorageItem(STORAGE_KEYS.TOKEN_EXPIRES_AT)
    const storedUser = safeParseJSON(getStorageItem(STORAGE_KEYS.USER))
    const storedRequiresSetup2FA = getStorageItem(STORAGE_KEYS.REQUIRES_SETUP_2FA)

    if (storedToken && storedUser && storedUser.id) {
      accessToken.value = storedToken
      refreshToken.value = storedRefreshToken
      tokenExpiresAt.value = storedExpiry ? parseInt(storedExpiry, 10) : null
      user.value = storedUser
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`
    }

    if (storedRequiresSetup2FA === 'true') {
      requiresSetup2FA.value = true
    }
  }

  // ============================================
  // AUTHENTICATION METHODS
  // ============================================

  /**
   * Step 1: Login with email/password
   */
  async function login(email, password) {
    try {
      const response = await api.post(`${API_BASE}/admin-auth`, { email, password })
      
      // User has 2FA enabled - need to verify with TOTP code
      if (response.data.requires_2fa) {
        pending2FA.value = true
        tempAdminId.value = response.data.admin_id
        return { requires2FA: true }
      }

      // User doesn't have 2FA enabled - check if setup is required
      if (!response.data.admin?.is_2fa_enabled) {
        requiresSetup2FA.value = true
        user.value = response.data.admin
        if (response.data.access_token) {
          setTokens(response.data)
        }
        return { requiresSetup2FA: true }
      }

      setAuthData(response.data)
      return { success: true }
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || 
                       error.response?.data?.error || 
                       error.message ||
                       'Login failed'
      throw errorMsg
    }
  }

  /**
   * Step 2: Verify 2FA code during login
   */
  async function verify2FA(code) {
    try {
      const response = await api.post(`${API_BASE}/admin-auth`, {
        admin_id: tempAdminId.value,
        totp_code: code
      })
      
      setAuthData(response.data)
      pending2FA.value = false
      tempAdminId.value = null
      return { success: true }
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || 
                       error.response?.data?.error || 
                       '2FA verification failed'
      throw errorMsg
    }
  }

  /**
   * Initiate 2FA setup - get TOTP URI
   */
  async function initiate2FASetup() {
    const adminId = user.value?.id
    if (!adminId) throw new Error('User not found')
    
    try {
      const response = await api.post(`${API_BASE}/two-factor`, { adminId })
      
      const totpUri = response.data.totp_uri
      const secretMatch = totpUri?.match(/secret=([A-Z2-7]+)/i)
      const secret = secretMatch ? secretMatch[1] : ''
      
      setup2FAData.value = { totpUri, secret }
      
      return { totpUri, secret }
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || 
                       error.response?.data?.message || 
                       'Failed to initiate 2FA setup'
      throw errorMsg
    }
  }

  /**
   * Set 2FA setup data (used when generating locally)
   */
  function setSetup2FAData(data) {
    setup2FAData.value = data
  }

  /**
   * Verify and enable 2FA
   */
  async function verify2FASetup(code) {
    const adminId = user.value?.id
    if (!adminId) throw new Error('User not found')
    
    try {
      const response = await api.put(`${API_BASE}/two-factor/${adminId}`, {
        action: 'verify',
        totpCode: code
      })
      
      // Update user to reflect 2FA is now enabled
      if (user.value) {
        user.value = { ...user.value, is_2fa_enabled: true }
      }
      
      if (response.data.admin) {
        user.value = response.data.admin
      }
      
      if (response.data.access_token) {
        setTokens(response.data)
      }
      
      requiresSetup2FA.value = false
      setup2FAData.value = null
      return { success: true }
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || 
                       error.response?.data?.error || 
                       '2FA verification failed'
      throw errorMsg
    }
  }

  /**
   * Disable 2FA
   */
  async function disable2FA() {
    const adminId = user.value?.id
    if (!adminId) throw new Error('User not found')
    
    try {
      await api.put(`${API_BASE}/two-factor/${adminId}`, { action: 'disable' })
      
      if (user.value) {
        user.value = { ...user.value, is_2fa_enabled: false }
      }
      
      return { success: true }
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || 
                       error.response?.data?.error || 
                       'Failed to disable 2FA'
      throw errorMsg
    }
  }

  // ============================================
  // TOKEN MANAGEMENT
  // ============================================

  /**
   * Store JWT tokens
   */
  function setTokens(data) {
    if (!data.access_token) {
      console.warn('setTokens called without access_token')
      return
    }

    accessToken.value = data.access_token
    refreshToken.value = data.refresh_token || refreshToken.value
    
    // Calculate expiration time
    const expiresIn = data.expires_in || 900 // Default 15 minutes
    tokenExpiresAt.value = Date.now() + (expiresIn * 1000)
  }

  /**
   * Set complete auth data (user + tokens)
   */
  function setAuthData(data) {
    if (!data.admin || !data.admin.id) {
      console.warn('setAuthData called without valid admin data')
      return
    }
    
    user.value = data.admin
    setTokens(data)
  }

  /**
   * Refresh access token
   */
  async function refreshAccessToken() {
    const currentRefreshToken = refreshToken.value || getStorageItem(STORAGE_KEYS.REFRESH_TOKEN)
    
    if (!currentRefreshToken) {
      throw new Error('No refresh token available')
    }
    
    try {
      const response = await api.post(`${API_BASE}/token-refresh`, {
        refresh_token: currentRefreshToken
      })
      
      setTokens(response.data)
      return { success: true }
    } catch (error) {
      // Refresh failed - clear auth
      clearAuth()
      throw new Error('Session expired. Please login again.')
    }
  }

  /**
   * Check if token needs refresh (within 1 minute of expiry)
   */
  function shouldRefreshToken() {
    if (!tokenExpiresAt.value) return false
    const bufferTime = 60 * 1000 // 1 minute buffer
    return Date.now() >= (tokenExpiresAt.value - bufferTime)
  }

  /**
   * Check if token is expired
   */
  function isTokenExpired() {
    if (!tokenExpiresAt.value) return true
    return Date.now() >= tokenExpiresAt.value
  }

  // ============================================
  // USER MANAGEMENT
  // ============================================

  /**
   * Fetch current user profile
   */
  async function fetchUser() {
    const currentToken = accessToken.value || getStorageItem(STORAGE_KEYS.ACCESS_TOKEN)
    
    if (!currentToken) {
      return false
    }

    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`
      const response = await api.get(`${API_BASE}/admin-auth/me`)
      
      if (response.data.admin && response.data.admin.id) {
        user.value = response.data.admin
        return true
      }
      
      return false
    } catch (error) {
      // If /me endpoint fails but we have stored user, keep using it
      const storedUser = safeParseJSON(getStorageItem(STORAGE_KEYS.USER))
      if (storedUser && storedUser.id) {
        user.value = storedUser
        return true
      }
      return false
    }
  }

  // ============================================
  // SESSION MANAGEMENT
  // ============================================

  /**
   * Logout user
   */
  async function logout() {
    try {
      if (accessToken.value) {
        await api.post(`${API_BASE}/admin-auth/logout`)
      }
    } catch (error) {
      console.warn('Logout API call failed:', error)
    } finally {
      clearAuth()
    }
  }

  /**
   * Clear all auth state
   */
  function clearAuth() {
    accessToken.value = null
    refreshToken.value = null
    tokenExpiresAt.value = null
    user.value = null
    pending2FA.value = false
    requiresSetup2FA.value = false
    tempAdminId.value = null
    setup2FAData.value = null
    
    clearAuthStorage()
    delete api.defaults.headers.common['Authorization']
  }

  // ============================================
  // PASSWORD MANAGEMENT
  // ============================================

  /**
   * Request password reset
   */
  async function forgotPassword(email) {
    try {
      await api.post(`${API_BASE}/admin-auth/forgot-password`, { email })
      return { success: true }
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || 
                       error.response?.data?.error || 
                       'Failed to send reset email'
      throw errorMsg
    }
  }

  /**
   * Reset password with token
   */
  async function resetPassword(resetToken, password, passwordConfirmation) {
    try {
      await api.post(`${API_BASE}/admin-auth/reset-password`, {
        token: resetToken,
        password,
        passwordConfirmation
      })
      return { success: true }
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || 
                       error.response?.data?.error || 
                       'Failed to reset password'
      throw errorMsg
    }
  }

  // ============================================
  // IMMEDIATE HYDRATION ON STORE CREATION
  // ============================================
  hydrate()

  // ============================================
  // EXPORTS
  // ============================================
  return {
    // State
    user,
    token,
    accessToken,
    refreshToken,
    pending2FA,
    requiresSetup2FA,
    tempAdminId,
    isInitialized,
    isInitializing,
    
    // Computed
    isAuthenticated,
    userRole,
    isSuperAdmin,
    isAdmin,
    isContentManager,
    isReviewer,
    
    // Initialization
    initialize,
    hydrate,
    
    // Auth methods
    login,
    verify2FA,
    initiate2FASetup,
    setSetup2FAData,
    verify2FASetup,
    disable2FA,
    logout,
    clearAuth,
    
    // Token methods
    refreshAccessToken,
    shouldRefreshToken,
    isTokenExpired,
    
    // User methods
    fetchUser,
    
    // Password methods
    forgotPassword,
    resetPassword
  }
})
